import React from 'react';
import styled from 'styled-components';
import { FiPhone, FiMail, FiMapPin, FiInstagram, FiLinkedin, FiGithub, FiHeart } from 'react-icons/fi';

const FooterContainer = styled.footer`
  background: var(--dark);
  color: var(--light);
  padding: 4rem 0 0;
  
  body.dark-mode & {
    background: #0a0a0a;
    border-top: 1px solid rgba(255, 255, 255, 0.05);
  }
  
  @media (max-width: 768px) {
    padding: 2.5rem 0 0;
  }
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: 1.5fr 1fr 1.2fr;
  gap: 3rem;
  max-width: 1100px;
  margin: 0 auto;
  padding: 0 2rem 3rem;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem 1.5rem;
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }
  
  @media (max-width: 576px) {
    padding: 0 1rem 1.5rem;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }
`;

const FooterColumn = styled.div`
  display: flex;
  flex-direction: column;
  
  &.left-column {
    @media (max-width: 576px) {
      grid-column: 1;
    }
  }
  
  &.right-column {
    @media (max-width: 576px) {
      grid-column: 2;
    }
  }
  
  &.hide-mobile {
    @media (max-width: 576px) {
      display: none;
    }
  }
`;

const FooterLogo = styled.a`
  font-size: 1.6rem;
  font-weight: 700;
  color: var(--white);
  margin-bottom: 0.75rem;
  display: inline-block;
  text-decoration: none;
  
  @media (max-width: 576px) {
    font-size: 1.3rem;
    margin-bottom: 0.5rem;
  }
  
  span {
    color: var(--secondary);
  }
`;

const FooterDescription = styled.p`
  color: var(--gray-400);
  margin-bottom: 1rem;
  line-height: 1.6;
  font-size: 0.9rem;
  
  @media (max-width: 576px) {
    display: none;
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 0.75rem;
  
  @media (max-width: 576px) {
    gap: 0.5rem;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.08);
  color: var(--gray-300);
  transition: all 0.3s ease;
  
  @media (max-width: 576px) {
    width: 32px;
    height: 32px;
    border-radius: 6px;
    
    svg {
      font-size: 0.9rem;
    }
  }
  
  &:hover {
    background: var(--secondary);
    color: var(--dark);
    transform: translateY(-3px);
  }
`;

const FooterTitle = styled.h3`
  color: var(--white);
  font-size: 1.1rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  position: relative;
  
  @media (max-width: 576px) {
    margin-bottom: 0.5rem;
    font-size: 0.9rem;
  }
`;

const FooterLinks = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  
  @media (max-width: 576px) {
    gap: 0.5rem;
  }
`;

const FooterLink = styled.a`
  color: var(--gray-400);
  transition: all 0.3s ease;
  font-size: 0.95rem;
  text-decoration: none;
  
  @media (max-width: 576px) {
    font-size: 0.8rem;
  }
  
  &:hover {
    color: var(--secondary);
    padding-left: 5px;
    
    @media (max-width: 576px) {
      padding-left: 0;
    }
  }
`;

const ContactList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  
  @media (max-width: 576px) {
    gap: 0.5rem;
    margin-bottom: 0.75rem;
  }
`;

const ContactItem = styled.a`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: var(--gray-400);
  font-size: 0.95rem;
  text-decoration: none;
  transition: all 0.3s ease;
  
  @media (max-width: 576px) {
    font-size: 0.75rem;
    gap: 0.4rem;
  }
  
  &:hover {
    color: var(--secondary);
  }
`;

const ContactIcon = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(254, 209, 54, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  
  svg {
    color: var(--secondary);
    font-size: 1rem;
  }
  
  @media (max-width: 576px) {
    width: 26px;
    height: 26px;
    border-radius: 5px;
    
    svg {
      font-size: 0.75rem;
    }
  }
`;

const Copyright = styled.div`
  text-align: center;
  padding: 1.5rem 2rem;
  background: rgba(0, 0, 0, 0.2);
  color: var(--gray-500);
  font-size: 0.85rem;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  
  @media (max-width: 576px) {
    padding: 1rem;
    font-size: 0.8rem;
  }
  
  svg {
    color: #ef4444;
    font-size: 0.9rem;
  }
  
  a {
    color: var(--secondary);
    text-decoration: none;
    font-weight: 500;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  const scrollToSection = (e, sectionId) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  return (
    <FooterContainer id="footer">
      <FooterContent>
        {/* Colonne gauche sur mobile: Logo + Contact + Social */}
        <FooterColumn className="left-column">
          <FooterLogo href="#accueil" onClick={(e) => scrollToSection(e, 'accueil')}>
            GVW<span>-Tech</span>
          </FooterLogo>
          <FooterDescription>
            Développeur web freelance basé en Belgique. Je crée des sites web modernes, performants et sur mesure pour donner vie à vos projets.
          </FooterDescription>
          
          {/* Contact info visible sur mobile */}
          <ContactList>
            <ContactItem href="tel:+32471229114">
              <ContactIcon>
                <FiPhone />
              </ContactIcon>
              +32 471 22 91 14
            </ContactItem>
            <ContactItem href="mailto:mail@gvw-tech.be">
              <ContactIcon>
                <FiMail />
              </ContactIcon>
              mail@gvw-tech.be
            </ContactItem>
          </ContactList>
          
          <SocialLinks>
            <SocialLink href="https://instagram.com" target="_blank" rel="noopener noreferrer" aria-label="Instagram">
              <FiInstagram />
            </SocialLink>
            <SocialLink href="https://linkedin.com" target="_blank" rel="noopener noreferrer" aria-label="LinkedIn">
              <FiLinkedin />
            </SocialLink>
            <SocialLink href="https://github.com" target="_blank" rel="noopener noreferrer" aria-label="GitHub">
              <FiGithub />
            </SocialLink>
          </SocialLinks>
        </FooterColumn>
        
        {/* Colonne droite sur mobile: Navigation */}
        <FooterColumn className="right-column">
          <FooterTitle>Navigation</FooterTitle>
          <FooterLinks>
            <FooterLink href="#accueil" onClick={(e) => scrollToSection(e, 'accueil')}>Accueil</FooterLink>
            <FooterLink href="#services" onClick={(e) => scrollToSection(e, 'services')}>Services</FooterLink>
            <FooterLink href="#realisations" onClick={(e) => scrollToSection(e, 'realisations')}>Réalisations</FooterLink>
            <FooterLink href="#a-propos" onClick={(e) => scrollToSection(e, 'a-propos')}>À Propos</FooterLink>
            <FooterLink href="#contact" onClick={(e) => scrollToSection(e, 'contact')}>Contact</FooterLink>
          </FooterLinks>
        </FooterColumn>
        
        {/* Colonne Contact - cachée sur mobile car déjà dans la colonne gauche */}
        <FooterColumn className="hide-mobile">
          <FooterTitle>Contact</FooterTitle>
          <ContactList>
            <ContactItem href="tel:+32471229114">
              <ContactIcon>
                <FiPhone />
              </ContactIcon>
              +32 471 22 91 14
            </ContactItem>
            <ContactItem href="mailto:mail@gvw-tech.be">
              <ContactIcon>
                <FiMail />
              </ContactIcon>
              mail@gvw-tech.be
            </ContactItem>
            <ContactItem 
              href="https://maps.google.com/?q=1017+Chaussée+de+Marche+5100+Wierde+Belgique" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <ContactIcon>
                <FiMapPin />
              </ContactIcon>
              Wierde, Belgique
            </ContactItem>
          </ContactList>
        </FooterColumn>
      </FooterContent>
      
      <Copyright>
        © {currentYear} GVW-Tech. Fait avec <FiHeart /> en Belgique.
      </Copyright>
    </FooterContainer>
  );
};

export default Footer;
